import axios from 'axios';
import Wrapper from '../../../components/Wrapper';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Office } from '../../../models/office';
import Title from '../../../components/Title';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom';
import useAxiosInterceptor from '../../AxiosInterceptor';
import { Box, CardActions, Grid, Button, Divider, Typography, Checkbox, CardContent, Alert } from '@mui/material';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import { SimpleColumn } from '../../../components/table/interface';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const EmployeOffice: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  useAxiosInterceptor();
  const navigate = useNavigate();
  const [selectedOffice, setSelectedOffice] = useState<number[]>([]);
  const [unselectedOffice, setUnselectedOffice] = useState<Office[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams();
  const [showError, setShowError] = useState(false);
  const [error, setError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [success, setSuccess] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch offices
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const { data } = await axios.get('offices');
        setOffices(data.data);
      } catch (error) {
        console.error('Error fetching offices: ', error);
      }
    };

    fetchOffices();
  }, []);

  // Fetch user offices
  useEffect(() => {
    const fetchUserOffices = async () => {
      try {
        const { data } = await axios.get(`useroffice/${id}`);
        setSelectedOffice(data.users.map((userOffice: { office_id: number }) => userOffice.office_id));
      } catch (error) {
        console.error('Error fetching user offices: ', error);
      }
    };

    fetchUserOffices();
  }, [id]);

  const handleCheckboxChange = (officeId: number, isActive: boolean) => {
    if (isActive) {
      setSelectedOffice((prevSelected: number[]) => [...prevSelected, officeId]);
      setUnselectedOffice((prevUnselected: any[]) => prevUnselected.filter(item => item.id !== officeId));
    } else {
      setSelectedOffice((prevSelected: number[]) => prevSelected.filter(id => id !== officeId));
      setUnselectedOffice((prevUnselected: any[]) => {
        if (!prevUnselected.map(item => item.id || item).includes(officeId)) {
          return [...prevUnselected, { id: officeId }];
        }
        return prevUnselected;
      });
    }
  };

  const handleSelectAll = (isActive: boolean) => {
    if (isActive) {
      const allOfficeIds = offices.map(office => office.id);
      setSelectedOffice(allOfficeIds);
      setUnselectedOffice([]);
    } else {
      setSelectedOffice([]);
      setUnselectedOffice(offices);
    }
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const setUnselectedOffices = unselectedOffice.map((office) => office.id);
    const formData = {
      officeId: selectedOffice,
      setUnselectedOffice: setUnselectedOffices,
      userId: id
    };
    try {
      const response = await axios.post('/useroffice', formData);
      navigate('/administrar/usuarios');
      setSuccess('Registros realizados exitosamente');
      setShowSuccess(true);
    } catch (error) {
      console.error('Error:', error);
      setShowError(true);
    }
  };

  const columns: SimpleColumn[] = [
    {
      field: 'name',
      headerName: 'Nombre',
      minWidth: 240,
      wrapText: true,
    },
    {
      field: 'options',
      headerName: 'Seleccionar',
      align: 'center',
      minWidth: 200,
      renderHeader: () => (
        <Checkbox
          indeterminate={selectedOffice.length > 0 && selectedOffice.length < offices.length}
          checked={selectedOffice.length === offices.length}
          onChange={(event) => handleSelectAll(event.target.checked)}
        />
      ),
      renderCell: ({ row }) => (
        <Checkbox
          checked={selectedOffice.includes(Number(row.id))}
          onChange={(event) => handleCheckboxChange(Number(row.id), event.target.checked)}
        />
      ),
    },
  ];

  const rows = offices.map((office: Office) => ({
    ...office,
    name: office.name,
  }));

  return (
    <HeaderResponsive>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Title>Asignar Usuarios a Oficinas</Title>
      </Box>
      <Grid item xs={12}>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          Seleccionar Oficinas
        </Typography>
      </Grid>
      {showError && (
        <Grid container alignItems="center" justifyContent="center" height="100%">
          <Grid item xs={12} sm={8} md={5}>
            <Alert severity="error" onClick={() => setShowError(false)}>
              {error}
            </Alert>
          </Grid>
        </Grid>
      )}
      {showSuccess && (
        <Grid container alignItems="center" justifyContent="center" height="100%">
          <Grid item xs={12} sm={8} md={5}>
            <Alert severity="success" onClick={() => setShowSuccess(false)}>
              {success}
            </Alert>
          </Grid>
        </Grid>
      )}
      <CardContent>
        <Grid container spacing={1} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <div style={{ height: '100%', width: '100%', alignItems: 'center' }}>
              {offices && offices.length > 0 ? (
                <SimpleCustomTable
                  rows={rows}
                  columns={columns}
                  maxHeight={400}
                  showBorders
                  wrapCellContent
                />
              ) : (
                ""
              )}
            </div>
          </Grid>
        </Grid>
      </CardContent>
      <Divider sx={{ margin: 0 }} />
      <CardActions>
        <Button size='small' type='submit' sx={{ mr: 2 }} variant='contained' onClick={submit}>
          Guardar
        </Button>
        <Link to='/administrar/usuarios'>
          <Button size='small' color='secondary' variant='outlined'>
            Cancelar
          </Button>
        </Link>
      </CardActions>
    </HeaderResponsive>
  );
};

export default EmployeOffice;
